import React from 'react'
import { Link } from 'gatsby'

import {
  Section,
  Grid,
  GridItem,
  SectionTitle,
  Intro,
} from '../../../../components'

import './colors.scss'
import data from './data.json'

function styles(hex) {
  if (hex === '#FFFFFF') {
    return {
      background: hex,
      boxShadow: '0 0 5px rgba(0,0,0,.1)',
    }
  } else
    return {
      background: hex,
    }
}

const Colors = () => {
  return (
    <>
      <Intro noBorder={true}>
        <p>
          A unified color palette brings consistency to all of M&T’s digital
          products. This anchors brand expression and helps the user navigate
          the experience with confidence. Limiting each color to its specific
          use case makes every layout easier to read at a glance.
        </p>
      </Intro>
      <Section type="gray">
        <Grid>
          <GridItem columns="12">
            <SectionTitle>Accessibility</SectionTitle>
          </GridItem>
          <GridItem columns="6">
            <p>
              The colors in this palette and their usages were chosen following
              the{' '}
              <a className="link" href="https://www.w3.org/TR/WCAG22/">
                Web Content Accessibility Guidelines (WCAG 2.2) Level AA
                Standard
              </a>{' '}
              of the Web Accessibility Initiative of the World Wide Web
              Consortium (W3C).
            </p>
          </GridItem>
          <GridItem columns="6">
            <p>
              To test whether a given combination of these colors passes this
              standard,{' '}
              <a
                className="link"
                href="https://webaim.org/resources/contrastchecker/"
              >
                visit this free contrast checker
              </a>
              . Color contrast is an important consideration in designing
              accessible experiences, especially for text and background
              combinations.
            </p>
          </GridItem>
        </Grid>
      </Section>
      <SectionTitle>
        Primary <span class="unbold">Colors</span>
      </SectionTitle>
      <p class="width-60">
        The palette’s primary colors reinforce M&T’s brand association with
        green. These colors will draw the user to the most important actions or
        information in a default layout.
      </p>
      <div className="colors">
        {data.colors.brand.primary.map(item => (
          <div className="colors__card" key={item.hex}>
            <div className="colors__swatch" style={styles(item.hex)} />
            <div className="colors__info">
              <div className="colors__item">
                <div className="colors__name">{item.name}</div>
              </div>
              <div className="colors__item">
                <div className="colors__hex">{item.hex}</div>
              </div>
              <div className="colors__item">
                <div className="colors__token">{item.token}</div>
              </div>
              <div className="colors__item">
                {item.uses.map(uses => (
                  <div className="colors__uses">{uses}</div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
      <hr />
      <SectionTitle>
        Secondary <span class="unbold">Colors</span>
      </SectionTitle>
      <p class="width-60">
        The palette’s secondary color is used to accent a layout. Use this color
        sparingly to draw a user's eye to certain layout elements.
      </p>
      <div className="colors colors--medium">
        {data.colors.brand.secondary.map(item => (
          <div className="colors__card" key={item.hex}>
            <div className="colors__swatch" style={styles(item.hex)} />
            <div className="colors__info">
              <div className="colors__item">
                <div className="colors__name">{item.name}</div>
              </div>
              <div className="colors__item">
                <div className="colors__hex">{item.hex}</div>
              </div>
              <div className="colors__item">
                <div className="colors__token">{item.token}</div>
              </div>
              <div className="colors__item">
                {item.uses.map(uses => (
                  <div className="colors__uses">{uses}</div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
      <hr />
      <SectionTitle>
        Semantic <span class="unbold">Colors</span>
      </SectionTitle>
      <p class="width-60">
        The palette’s semantic colors communicate feedback and status using
        intuitive color associations. Refer to the{' '}
        <Link
          class="link"
          to={'/' + data.site + '/components/notifications/design'}
        >
          notifications
        </Link>{' '}
        component or the{' '}
        <Link
          class="link"
          to={'/' + data.site + '/guidelines/feedback-errors/design'}
        >
          feedback and errors
        </Link>{' '}
        and{' '}
        <Link class="link" to={'/' + data.site + '/guidelines/status/design'}>
          status
        </Link>{' '}
        guidelines for more on proper color usage.
      </p>
      <div class="colors__container">
        <div className="colors colors--group">
          {data.colors.brand.semantic.oranges.map(item => (
            <div className="colors__card" key={item.hex}>
              <div className="colors__swatch" style={styles(item.hex)} />
              <div className="colors__info">
                <div className="colors__item">
                  <div className="colors__name">{item.name}</div>
                </div>
                <div className="colors__item">
                  <div className="colors__hex">{item.hex}</div>
                </div>
                <div className="colors__item">
                  <div className="colors__token">{item.token}</div>
                </div>
                <div className="colors__item">
                  {item.uses.map(uses => (
                    <div className="colors__uses">{uses}</div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="colors colors--group">
          {data.colors.brand.semantic.reds.map(item => (
            <div className="colors__card" key={item.hex}>
              <div className="colors__swatch" style={styles(item.hex)} />
              <div className="colors__info">
                <div className="colors__item">
                  <div className="colors__name">{item.name}</div>
                </div>
                <div className="colors__item">
                  <div className="colors__hex">{item.hex}</div>
                </div>
                <div className="colors__item">
                  <div className="colors__token">{item.token}</div>
                </div>
                <div className="colors__item">
                  {item.uses.map(uses => (
                    <div className="colors__uses">{uses}</div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="colors colors--group">
          {data.colors.brand.semantic.greens.map(item => (
            <div className="colors__card" key={item.hex}>
              <div className="colors__swatch" style={styles(item.hex)} />
              <div className="colors__info">
                <div className="colors__item">
                  <div className="colors__name">{item.name}</div>
                </div>
                <div className="colors__item">
                  <div className="colors__hex">{item.hex}</div>
                </div>
                <div className="colors__item">
                  <div className="colors__token">{item.token}</div>
                </div>
                <div className="colors__item">
                  {item.uses.map(uses => (
                    <div className="colors__uses">{uses}</div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="colors colors--group">
          {data.colors.brand.semantic.blues.map(item => (
            <div className="colors__card" key={item.hex}>
              <div className="colors__swatch" style={styles(item.hex)} />
              <div className="colors__info">
                <div className="colors__item">
                  <div className="colors__name">{item.name}</div>
                </div>
                <div className="colors__item">
                  <div className="colors__hex">{item.hex}</div>
                </div>
                <div className="colors__item">
                  <div className="colors__token">{item.token}</div>
                </div>
                <div className="colors__item">
                  {item.uses.map(uses => (
                    <div className="colors__uses">{uses}</div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <hr />
      <SectionTitle>
        Grayscale <span class="unbold">Colors</span>
      </SectionTitle>
      <p class="width-60">
        The palette’s grayscale colors fill in the rest of the visual elements
        in a layout, including text, subtle backgrounds, and borders.
      </p>
      <div className="colors colors--group colors--small">
        {data.colors.brand.grayscale.grays.map(item => (
          <div className="colors__card" key={item.hex}>
            <div className="colors__swatch" style={styles(item.hex)} />
            <div className="colors__info">
              <div className="colors__item">
                <div className="colors__name">{item.name}</div>
              </div>
              <div className="colors__item">
                <div className="colors__hex">{item.hex}</div>
              </div>
              <div className="colors__item">
                <div className="colors__token">{item.token}</div>
              </div>
              <div className="colors__item">
                {item.uses.map(uses => (
                  <div className="colors__uses">{uses}</div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="colors colors--group  colors--small">
        {data.colors.brand.grayscale.blackwhite.map(item => (
          <div className="colors__card" key={item.hex}>
            <div className="colors__swatch" style={styles(item.hex)} />
            <div className="colors__info">
              <div className="colors__item">
                <div className="colors__name">{item.name}</div>
              </div>
              <div className="colors__item">
                <div className="colors__hex">{item.hex}</div>
              </div>
              <div className="colors__item">
                <div className="colors__token">{item.token}</div>
              </div>
              <div className="colors__item">
                {item.uses.map(uses => (
                  <div className="colors__uses">{uses}</div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  )
}
export default Colors
